import { useEffect, useMemo, useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import {
  SAMPLE_RAG_QUERIES,
  assembleContextPrompt,
  getPortfolioChunkCount,
  getPortfolioRagIndex,
} from "../../lib/rag/portfolioCorpus";

const DEMO_STORAGE_KEY = "portfolio-rag-demo-open";

const PIPELINE = ["Chunk", "Index", "Query", "Retrieve", "Context"];

const AIDemoSection = ({ darkMode = false }) => {
  const chunkCount = useMemo(() => getPortfolioChunkCount(), []);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(SAMPLE_RAG_QUERIES[0]);
  const [submittedQuery, setSubmittedQuery] = useState(SAMPLE_RAG_QUERIES[0]);
  const [topK, setTopK] = useState(3);
  const [showContext, setShowContext] = useState(false);

  useEffect(() => {
    const fromHash = typeof window !== "undefined" && window.location.hash === "#ai-demo";
    try {
      const stored = window.sessionStorage.getItem(DEMO_STORAGE_KEY);
      if (fromHash || stored === "1") setIsOpen(true);
    } catch {
      if (fromHash) setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(DEMO_STORAGE_KEY, isOpen ? "1" : "0");
    } catch {
      // Ignore storage failures
    }
  }, [isOpen]);

  const hits = useMemo(() => {
    if (!isOpen) return [];
    return getPortfolioRagIndex().search(submittedQuery, { topK });
  }, [isOpen, submittedQuery, topK]);

  const maxScore = hits[0]?.score || 1;
  const contextPrompt = useMemo(
    () => assembleContextPrompt(submittedQuery, hits),
    [submittedQuery, hits]
  );

  const runSearch = (nextQuery = query) => {
    const trimmed = nextQuery.trim();
    if (!trimmed) return;
    setQuery(trimmed);
    setSubmittedQuery(trimmed);
    if (!isOpen) setIsOpen(true);
  };

  const toggleOpen = () => setIsOpen((current) => !current);

  return (
    <section
      id="ai-demo"
      className={`rag-demo-section py-12 sm:py-14 transition-colors duration-500 ${
        darkMode ? "bg-stone-950 text-white" : "bg-[#f4f1ea] text-stone-950"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div
          className={`rounded-2xl border ${
            darkMode ? "border-stone-700 bg-stone-900" : "border-stone-950/10 bg-white"
          }`}
        >
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="min-w-0">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-teal-800 dark:text-teal-300">
                Live RAG demo
              </p>
              <h2 className="mt-2 text-2xl font-black leading-tight sm:text-3xl">
                Ask this portfolio
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-7 text-stone-600 dark:text-stone-400">
                In-browser BM25 retrieval over my experience data — {chunkCount} chunks, no API key,
                no token cost. Expand to try it.
              </p>
            </div>
            <button
              type="button"
              onClick={toggleOpen}
              aria-expanded={isOpen}
              aria-controls="rag-demo-panel"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-teal-700 px-5 py-3 text-sm font-bold text-white transition hover:bg-teal-800"
            >
              {isOpen ? (
                <>
                  Minimize demo <FaChevronUp size={12} />
                </>
              ) : (
                <>
                  Open demo <FaChevronDown size={12} />
                </>
              )}
            </button>
          </div>

          {isOpen && (
            <div
              id="rag-demo-panel"
              className={`border-t px-5 py-5 sm:px-6 sm:py-6 ${
                darkMode ? "border-stone-700" : "border-stone-950/10"
              }`}
            >
              <ol className="flex flex-wrap gap-2 text-xs font-semibold">
                {PIPELINE.map((step, index) => (
                  <li
                    key={step}
                    className={`rounded-full px-3 py-1.5 ${
                      darkMode ? "bg-stone-800 text-stone-300" : "bg-[#f4f1ea] text-stone-700"
                    }`}
                  >
                    <span className="text-teal-700 dark:text-teal-300">{index + 1}.</span> {step}
                  </li>
                ))}
              </ol>

              <div className="mt-5 grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
                <div>
                  <label
                    htmlFor="rag-query"
                    className="text-xs font-black uppercase tracking-[0.2em] text-teal-800 dark:text-teal-300"
                  >
                    Question
                  </label>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <input
                      id="rag-query"
                      type="text"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") runSearch();
                      }}
                      className={`w-full rounded-xl border px-4 py-3 text-sm outline-none transition focus:border-teal-600 ${
                        darkMode
                          ? "border-stone-600 bg-stone-950 text-white placeholder:text-stone-500"
                          : "border-stone-300 bg-[#f8f6f1] text-stone-950 placeholder:text-stone-500"
                      }`}
                      placeholder="Ask about Allianz, RAG, Azure, agents…"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => runSearch()}
                      className="shrink-0 rounded-xl bg-stone-950 px-5 py-3 text-sm font-bold text-white transition hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500"
                    >
                      Retrieve
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {SAMPLE_RAG_QUERIES.map((sample) => (
                      <button
                        key={sample}
                        type="button"
                        onClick={() => runSearch(sample)}
                        className={`rounded-full border px-3 py-1.5 text-left text-xs font-semibold transition ${
                          submittedQuery === sample
                            ? darkMode
                              ? "border-teal-400 bg-teal-950 text-teal-200"
                              : "border-teal-700 bg-teal-50 text-teal-900"
                            : darkMode
                              ? "border-stone-600 text-stone-300"
                              : "border-stone-300 text-stone-700"
                        }`}
                      >
                        {sample}
                      </button>
                    ))}
                  </div>

                  <div className="mt-5">
                    <label
                      htmlFor="rag-topk"
                      className="flex items-center justify-between text-xs font-bold text-stone-600 dark:text-stone-400"
                    >
                      <span>Top-k chunks</span>
                      <span>{topK}</span>
                    </label>
                    <input
                      id="rag-topk"
                      type="range"
                      min="1"
                      max="5"
                      value={topK}
                      onChange={(event) => setTopK(Number(event.target.value))}
                      className="mt-2 w-full accent-teal-700"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div
                    className={`rounded-xl border p-4 ${
                      darkMode ? "border-stone-700 bg-stone-950/60" : "border-stone-950/10 bg-[#f8f6f1]"
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-black">Retrieved chunks</h3>
                      <p className="text-xs font-semibold text-stone-500">
                        {hits.length
                          ? `${hits.length} hit${hits.length === 1 ? "" : "s"}`
                          : "No matches"}
                      </p>
                    </div>
                    <ul className="mt-3 max-h-[320px] space-y-3 overflow-y-auto pr-1">
                      {hits.length === 0 && (
                        <li className="rounded-lg border border-dashed border-stone-400/40 p-3 text-sm text-stone-500">
                          Try a sample question, or terms like RAG, Allianz, Azure, or Flowise.
                        </li>
                      )}
                      {hits.map((hit, index) => (
                        <li
                          key={hit.id}
                          className={`rounded-lg border p-3 ${
                            darkMode ? "border-stone-700 bg-stone-900" : "border-stone-950/10 bg-white"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-[0.65rem] font-black uppercase tracking-[0.16em] text-teal-800 dark:text-teal-300">
                                #{index + 1} · {hit.source}
                              </p>
                              <h4 className="mt-1 text-sm font-bold">{hit.title}</h4>
                            </div>
                            <span className="shrink-0 rounded-full bg-teal-700/10 px-2.5 py-1 text-[0.65rem] font-bold text-teal-800 dark:text-teal-300">
                              {hit.score.toFixed(2)}
                            </span>
                          </div>
                          <div className="mt-2 h-1 overflow-hidden rounded-full bg-stone-300/40">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-teal-600 to-amber-400"
                              style={{ width: `${Math.max(8, (hit.score / maxScore) * 100)}%` }}
                            />
                          </div>
                          <p className="mt-2 line-clamp-3 text-xs leading-6 text-stone-600 dark:text-stone-400">
                            {hit.text}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() => setShowContext((current) => !current)}
                      className="text-xs font-bold uppercase tracking-[0.16em] text-teal-800 dark:text-teal-300"
                      aria-expanded={showContext}
                    >
                      {showContext ? "Hide assembled context" : "Show assembled context"}
                    </button>
                    {showContext && (
                      <pre className="rag-context-preview mt-3 overflow-x-auto rounded-xl p-4 text-xs leading-6">
                        {contextPrompt}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AIDemoSection;
